import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export const ProductCard = ({ product, index }) => {
  const { add } = useCart();
  const hasOptions = Boolean(product.variants?.length || product.packs?.length);
  const url = `/product/${product.slug}`;
  const dealPct = product.mrp > product.price ? Math.round((1 - product.price / product.mrp) * 100) : 0;
  const outOfStock = (product.stock ?? 100) <= 0;

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08 }}
      className="group bg-white/70 backdrop-blur-sm border border-border/60 rounded-xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 transition-all duration-500"
    >
      <Link to={url} data-testid={`product-link-${product.slug}`} className="block relative aspect-square overflow-hidden bg-white">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-contain p-3 transition-transform duration-700 group-hover:scale-105"
        />
        {product.badge && (
          <span className="absolute top-3 left-3 bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        {dealPct > 0 && (
          <div className="absolute top-0 right-0 w-28 h-28 overflow-hidden pointer-events-none" data-testid={`deal-ribbon-${product.slug}`}>
            <span className="absolute top-[20px] -right-[38px] rotate-45 bg-accent text-accent-foreground text-[8px] font-bold uppercase tracking-[0.14em] px-10 py-1 shadow-md whitespace-nowrap">
              Deal of the Day
            </span>
          </div>
        )}
        {outOfStock && (
          <span data-testid={`stock-soon-${product.slug}`} className="absolute inset-x-3 bottom-3 rounded-full bg-foreground/85 text-background text-[10px] uppercase tracking-[0.14em] font-bold py-2.5 text-center">
            Stock coming soon
          </span>
        )}
        <div className="absolute inset-x-3 bottom-3 flex gap-2 sm:translate-y-14 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 transition-all duration-500">
          {outOfStock ? null : hasOptions ? (
            <span
              data-testid={`quick-add-${product.slug}`}
              className="flex-1 rounded-full bg-primary text-primary-foreground text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 hover:brightness-110 transition"
            >
              <Plus className="w-3.5 h-3.5" /> Select options
            </span>
          ) : (
            <button
              data-testid={`quick-add-${product.slug}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                add(product);
              }}
              className="flex-1 rounded-full bg-primary text-primary-foreground text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 hover:brightness-110 transition"
            >
              <Plus className="w-3.5 h-3.5" /> Add to cart
            </button>
          )}
        </div>
      </Link>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary">{product.category}</p>
        <Link to={url} className="hover:text-primary transition-colors">
          <h3 className="font-serif text-lg font-medium mt-1 leading-snug">{product.name}</h3>
        </Link>
        {product.rating_count > 0 && (
          <p data-testid={`rating-${product.slug}`} className="mt-1 flex items-center gap-1 text-xs font-bold text-amber-600">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            {product.rating_average} <span className="text-muted-foreground font-normal">({product.rating_count})</span>
          </p>
        )}
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-bold text-foreground">
            {product.packs?.length ? `From ${inr(product.price)}` : inr(product.price)}
          </span>
          {product.mrp > product.price && (
            <span className="text-xs text-muted-foreground line-through">{inr(product.mrp)}</span>
          )}
          {dealPct > 0 && (
            <span data-testid={`deal-pct-${product.slug}`} className="text-xs font-bold text-accent">-{dealPct}%</span>
          )}
        </div>
        {product.min_qty > 1 && (
          <p data-testid={`min-set-${product.slug}`} className="mt-1.5 text-[10px] font-bold uppercase tracking-wider text-accent">
            Set of {product.min_qty}+ pcs · mix options
          </p>
        )}
        {product.packs?.length > 0 && (
          <p data-testid={`multipack-${product.slug}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            Multi-packs available
          </p>
        )}
      </div>
    </motion.article>
  );
};
